import { FusedRecord } from '../core/types/fusion';
import { TrackingResult } from '../core/types/tracking';
import { AudioFeatureWindow } from '../core/types/audio';
import { SessionMetadata } from '../core/types/session';

/**
 * Escapes CSV text fields properly for special characters, quotes, commas, and unicode
 */

export function escapeCsvCell(val: any): string {
  if (val === null || val === undefined) return '';
  const str = String(val);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes(';')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Generates Visual Tracking CSV
 */
export function exportVisualCsv(results: TrackingResult[], runId: string): string {
  const headers = [
    'schema_version',
    'run_id',
    'camera_id',
    'sequence',
    'observed_at',
    'monotonic_ms',
    'status',
    'center_x_raw',
    'center_y_raw',
    'center_x_filtered',
    'center_y_filtered',
    'bbox_x',
    'bbox_y',
    'bbox_width',
    'bbox_height',
    'area_px',
    'circularity',
    'radius_px',
    'lost_frame_count',
    'processing_latency_ms',
    'coordinate_space',
    'unit',
  ];

  const rows = results.map((r) => [
    '1.0.0',
    runId,
    r.rawPosition.cameraId,
    r.sequence,
    new Date(r.observedAt).toISOString(),
    r.monotonicMs.toFixed(2),
    r.status,
    r.rawPosition.x.toFixed(2),
    r.rawPosition.y.toFixed(2),
    r.filteredPosition.x.toFixed(2),
    r.filteredPosition.y.toFixed(2),
    r.bbox.x,
    r.bbox.y,
    r.bbox.width,
    r.bbox.height,
    r.areaPx,
    r.circularity.toFixed(4),
    r.radiusPx,
    r.lostFrameCount,
    r.processingLatencyMs.toFixed(2),
    r.rawPosition.coordinateFrame,
    r.rawPosition.unit,
  ]);

  return [headers.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
}

/**
 * Generates Audio Features CSV
 */
export function exportAudioCsv(features: AudioFeatureWindow[], runId: string): string {
  const headers = [
    'schema_version',
    'run_id',
    'sequence',
    'observed_at',
    'monotonic_ms',
    'rms',
    'dbfs',
    'dominant_frequency_hz',
    'spectral_centroid_hz',
    'zero_crossing_rate',
    'sample_count',
    'status',
    'quality_flags',
  ];

  const rows = features.map((f) => [
    '1.0.0',
    runId,
    f.sequence,
    new Date(f.observedAt).toISOString(),
    f.monotonicMs.toFixed(2),
    f.rms.toFixed(6),
    f.dbfs.toFixed(1),
    f.dominantFrequencyHz,
    f.spectralCentroidHz,
    f.zeroCrossingRate.toFixed(4),
    f.sampleCount,
    f.status,
    f.qualityFlags.join(';'),
  ]);

  return [headers.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
}

/**
 * Generates Acoustic-Position Fused CSV
 */
export function exportFusedCsv(records: FusedRecord[]): string {
  const headers = [
    'schema_version',
    'run_id',
    'sequence',
    'observed_at',
    'monotonic_ms',
    'audio_dbfs',
    'dominant_frequency_hz',
    'spectral_centroid_hz',
    'cam_a_status',
    'cam_a_x',
    'cam_a_y',
    'cam_a_time_diff_ms',
    'cam_b_status',
    'cam_b_x',
    'cam_b_y',
    'cam_b_time_diff_ms',
    'match_status',
    'coordinate_space',
    'quality_flags',
  ];

  const rows = records.map((r) => [
    r.schemaVersion,
    r.runId,
    r.sequence,
    new Date(r.observedAt).toISOString(),
    r.monotonicMs.toFixed(2),
    r.audioFeatures.dbfs.toFixed(1),
    r.audioFeatures.dominantFrequencyHz,
    r.audioFeatures.spectralCentroidHz,
    r.cameraAPosition ? r.cameraAPosition.status : 'missing',
    r.cameraAPosition ? r.cameraAPosition.filteredPosition.x.toFixed(2) : '',
    r.cameraAPosition ? r.cameraAPosition.filteredPosition.y.toFixed(2) : '',
    r.cameraATimeDiffMs !== undefined ? r.cameraATimeDiffMs.toFixed(1) : '',
    r.cameraBPosition ? r.cameraBPosition.status : 'missing',
    r.cameraBPosition ? r.cameraBPosition.filteredPosition.x.toFixed(2) : '',
    r.cameraBPosition ? r.cameraBPosition.filteredPosition.y.toFixed(2) : '',
    r.cameraBTimeDiffMs !== undefined ? r.cameraBTimeDiffMs.toFixed(1) : '',
    r.matchStatus,
    r.coordinateSpace,
    r.qualityFlags.join(';'),
  ]);

  return [headers.join(','), ...rows.map((row) => row.map(escapeCsvCell).join(','))].join('\n');
}

/**
 * Triggers browser download of a text file (CSV/JSON)
 */
export function downloadFile(content: string, filename: string, mimeType = 'text/csv;charset=utf-8;') {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Downloads a HTML Canvas as a PNG Image
 */
export function downloadCanvasPng(canvas: HTMLCanvasElement, filename: string) {
  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 'image/png');
}

/**
 * Exports complete session as JSON
 */
export function exportSessionJson(metadata: SessionMetadata, records: FusedRecord[]): string {
  return JSON.stringify(
    {
      metadata,
      records,
    },
    null,
    2
  );
}
